"use client";

import type React from "react";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Lock, Mail, ArrowRight, Sparkles } from "lucide-react";
import Image from "next/image";
import { loginSchema, type LoginFormData } from "@/lib/schemas";
import { useLogin } from "@/lib/hooks/use-mutations";

export default function AdminLoginPage() {
  const [showPassword, setShowPassword] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "admin@admin.com",
      password: "pass123.%",
    },
  });

  const { mutate: loginMutation, isPending } = useLogin();

  const onSubmit = (data: LoginFormData) => {
    loginMutation(data);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(2,89,251,0.1),transparent)]"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-72 h-72 rounded-full blur-3xl animate-pulse bg-blue-500/20"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl animate-pulse bg-blue-500/15"></div>

      <div className="w-full max-w-md relative z-10">
        {/* Login Card */}
        <Card className="bg-slate-800/50 backdrop-blur-xl border-slate-700/50 shadow-2xl overflow-hidden">
          <div className="flex justify-center mb-6">
            <Image
              src="/assests/whiteLogo.png"
              alt="C-Me-Talk"
              width={200}
              height={100}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>

          <CardHeader className="space-y-2 pb-6 relative">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-blue-400" />
              <CardTitle className="text-2xl font-semibold text-white text-center">
                Administrator Login
              </CardTitle>
              <Sparkles className="w-5 h-5 text-blue-400" />
            </div>
            <CardDescription className="text-center text-slate-300">
              Enter your credentials to access the admin dashboard
            </CardDescription>
          </CardHeader>

          <CardContent className="relative">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Error Message */}
              {errors.root && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-400 text-sm">{errors.root.message}</p>
                </div>
              )}

              {/* Email Field */}
              <div className="space-y-3">
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-slate-200"
                >
                  Email Address
                </Label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                  <Input
                    {...register("email")}
                    id="email"
                    type="email"
                    placeholder="admin@cmetalk.com"
                    className={`pl-12 h-14 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20 focus:ring-2 transition-all duration-200 rounded-xl ${
                      errors.email ? "border-red-500" : ""
                    }`}
                    disabled={isPending}
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-3">
                <Label
                  htmlFor="password"
                  className="text-sm font-medium text-slate-200"
                >
                  Password
                </Label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-blue-400 transition-colors" />
                  <Input
                    {...register("password")}
                    id="password"
                    type={!showPassword ? "text" : "password"}
                    placeholder="Enter your secure password"
                    className={`pl-12 pr-12 h-14 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20 focus:ring-2 transition-all duration-200 rounded-xl ${
                      errors.password ? "border-red-500" : ""
                    }`}
                    disabled={isPending}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-10 w-10 p-0 hover:bg-slate-600/50 rounded-lg transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isPending}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-slate-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-slate-400" />
                    )}
                  </Button>
                  {errors.password && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-14 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                disabled={isPending}
              >
                {isPending ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Accessing Admin Panel...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <span>Access Admin Panel</span>
                    <ArrowRight className="h-5 w-5" />
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
